<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";
import { svgIcon, baseInput } from "@/common/components";

import { sampleList } from "@/common/api/rest/sample";
import { evaluationSampleList } from "@/common/api/rest/evaluation";

import type { SampleType } from "@/common/types/sample";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = defineProps<{
  title?: string;
  accountId?: string;
  evaluationId?: string;
  status?: string;
  unic?: boolean;
  ids?: string[];
}>();

const selected = ref<SampleType[]>([]);
const listData = ref<SampleType[]>([]);
const inputSearch = ref<string | null>("");

function searchEvaluation() {
  if (!props.evaluationId) return;

  evaluationSampleList(props.evaluationId, {
    search: inputSearch.value as string,
    status: props.status || null,
    full: true,
    page: 0,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        listData.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function searchSamples() {
  if (!props.accountId) return;

  sampleList(props.accountId, {
    search: inputSearch.value as string,
    status: props.status || "",
    full: true,
    page: 0,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        listData.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function search() {
  if (props.evaluationId) {
    searchEvaluation();
    return;
  }

  searchSamples();
}

function isDisabled(data: SampleType) {
  if (props.ids?.includes(data.sample_id)) {
    return true;
  }

  if (data.status === "IN_USE") {
    return true;
  }

  return false;
}

function select(data: SampleType) {
  if (isDisabled(data)) {
    return;
  }

  if (props.unic) {
    selected.value = [data];
  } else {
    if (selected.value.find((sl) => sl.sample_id === data.sample_id)) {
      selected.value = selected.value.filter(
        (sl) => sl.sample_id !== data.sample_id,
      );
    } else {
      selected.value.push(data);
    }
  }

  emit("update-data", selected.value);
}

onMounted(() => {
  search();
});
</script>

<template>
  <section class="technical-form">
    <div class="title">
      {{ title }}
    </div>
    <div class="search">
      <base-input
        v-model="inputSearch"
        background="#fff"
        color="#000"
        :placeholder="t('common.labels.search')"
        @key:enter="search()"
      >
        <template #sufix>
          <div class="icon">
            <svg-icon icon="search" size="1.75rem" @click="search()" />
          </div>
        </template>
      </base-input>
    </div>

    <div class="list">
      <template v-for="item in listData" :key="item.sample_id">
        <div
          class="item"
          @click="select(item)"
          :class="{
            active: selected.find((sl) => sl.sample_id === item.sample_id),
            disabled: isDisabled(item),
          }"
        >
          <div class="name">
            {{ item.code }}
          </div>
          <div class="type">
            {{ item.name }}
          </div>
        </div>
      </template>
      <template v-if="listData.length === 0">
        <div class="empty">
          {{ t("common.texts.no_results") }}
        </div>
      </template>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.technical-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  .search {
    width: 100%;

    .icon {
      cursor: pointer;
      color: var(--text-off);

      &:hover {
        color: var(--primary);
      }
    }
  }

  .list {
    display: flex;
    flex-direction: column;
    width: calc(100% - 0.5rem);
    height: 50dvh;
    overflow-y: scroll;
    padding-inline-end: 0.5rem;
    gap: 0.5rem;

    .item {
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      border: 1px solid var(--border);
      padding-block: 0.5rem;
      padding-inline: 1rem;
      border-radius: 0.5rem;

      &.active {
        border-color: var(--primary);
        background: var(--primary);
        color: var(--primary-color);
      }

      &.disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }

      .name {
        font-weight: bold;
      }
    }
  }
}
</style>
